import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone';
import { BreadcrumbService } from '../../services/breadcrumb/breadcrumb.service';
import { Subscription } from 'rxjs';
import { EnvironmentsEnum } from '../../enum/environments-enum';
import { environment } from '../../../environments/environment';
import { LanguageService } from '../../services/language/language.service';
import { WebcomponentInterfaceService } from '../../services/webcomponent-interface/webcomponent-interface.service';
import { ApiConfiguration } from '../../api/api-configuration';
import { faBookReader, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { ApplicationSettingsService } from '../../services/application-settings/application-settings.service';
import { StateNamesEnum } from '../../enum/state-names-enum';
import { SessionStorageService } from '../../services/session-storage/session-storage.service';
import { StateService } from '../../services/state/state.service';
import { ColorExtractorService } from '../skeleton-loader/color-extractor.service';
import { ApplicationLoadedService } from '../../services/application-loaded/application-loaded.service';
import { DesignFrontendSettings } from '../../api/models/design-frontend-settings';
import { ContentLinksService } from '../../services/content-links/content-links.service';
import { ContentPage } from '../../api/models/content-page';
import Utils from '../../utils';

@Component({
    selector: 'otb-header',
    templateUrl: './header.component.html',
    styleUrls: [
        './header.component.scss',
        './header.component.justice.scss',
        './header.component.finance.scss',
        './header.component.mags-sop.scss',
        './header.component.sh.scss',
        './header.component.hmdj.scss',
        './header.component.bb-jm.scss',
    ],
    standalone: false,
})
export class HeaderComponent implements OnDestroy, OnInit, AfterViewInit {
    @ViewChild('headerTop') headerTop: ElementRef;
    @ViewChild('langPickContainer') langPickContainer: ElementRef;
    @ViewChild('header') header: ElementRef;

    show: boolean = true;
    phoneIcon = faPhone;
    subs: Subscription = new Subscription();
    headerClasses: any;
    hasMultipleLanguages: boolean = false;
    headless: boolean;
    rootUrl = '';
    faPaperPlane = faPaperPlane;
    faBookReader = faBookReader;
    showEasyLanguagePage: boolean | undefined;
    showUploadedLogo: boolean | undefined;
    header1Color = '';
    header2Color = '';
    header1TextColor: string | null = null;
    header2TextColor: string | null = null;
    contentPages: Array<ContentPage> = [];
    leftPages: Array<ContentPage> = [];
    rightPages: Array<ContentPage> = [];

    errorLoadingApplicationSettings: Signal<boolean>;
    appLoaded: Signal<boolean>;
    constructor(
        private breadcrumbService: BreadcrumbService,
        private langService: LanguageService,
        private webcomponentInterfaceService: WebcomponentInterfaceService,
        private apiConfiguration: ApiConfiguration,
        private applicationSettingsService: ApplicationSettingsService,
        private sessionStorageService: SessionStorageService,
        private stateService: StateService,
        private colorExtractorService: ColorExtractorService,
        private applicationLoadedService: ApplicationLoadedService,
        private contentLinksService: ContentLinksService,
    ) {
        this.headerClasses = {
            'container p-0': this.hasSmallHeader(),
        };

        this.rootUrl = this.apiConfiguration.rootUrl;

        const languages$ = this.langService.allLanguages.subscribe((languages) => {
            this.hasMultipleLanguages = languages.length > 1;
        });

        this.subs.add(languages$);

        this.errorLoadingApplicationSettings = this.applicationSettingsService.errorLoadingApplicationSettings;
        this.appLoaded = this.applicationLoadedService.applicationLoaded;
    }

    ngAfterViewInit(): void {
        this.extractColorFromHeader();

        if (environment.deploy_environment === EnvironmentsEnum.MAGS_SOP) {
            this.setElementsHeight();
        }
    }

    private extractColorFromHeader(): void {
        this.colorExtractorService.headerColor.set('transparent');
    }

    private initialize(): void {
        this.subs.add(
            this.applicationSettingsService.designFrontendSettingsSubject.subscribe(
                (settings: DesignFrontendSettings | null) => {
                    this.header1Color = settings?.color_header1 ?? '';
                    this.header2Color = settings?.color_header2 ?? '';
                    this.header1TextColor = Utils.contrastColor(this.header1Color);
                    this.header2TextColor = Utils.contrastColor(this.header2Color);
                },
            ),
        );
        this.subs.add(
            this.applicationSettingsService.showEasyLanguagePageSubject.subscribe((show: boolean) => {
                this.showEasyLanguagePage = show ?? false;
            }),
        );
        this.subs.add(
            this.applicationSettingsService.showUploadedLogoSubject.subscribe((show: boolean) => {
                this.showUploadedLogo = show ?? false;
            }),
        );
    }

    private hasSmallHeader(): boolean {
        return environment.deploy_environment === EnvironmentsEnum.SH; // refactor when more than one customer has a small header
    }

    private retrieveContentPages() {
        const getHeaderContentPages = this.contentLinksService.headerContentPages$.subscribe(
            (contentPage: Array<ContentPage>) => {
                if (contentPage.length > 0) {
                    this.contentPages = this.contentLinksService.sortContentPages(contentPage);
                    this.leftPages = this.contentPages.filter((page) => page.alignment === 'left');
                    this.rightPages = this.contentPages.filter((page) => page.alignment === 'right');
                }
            },
        );

        this.subs.add(getHeaderContentPages);
    }

    private setElementsHeight() {
        const MARGIN_TOP = 12;
        this.subs = this.breadcrumbService.breadcrumbHeight.subscribe((height: number) => {
            if (height > 0 && this.headerTop?.nativeElement && this.langPickContainer?.nativeElement) {
                this.langPickContainer.nativeElement.style.top = height + MARGIN_TOP + 'px';
                this.headerTop.nativeElement.style.height =
                    height + MARGIN_TOP + this.langPickContainer.nativeElement.offsetHeight + MARGIN_TOP + 'px';
            }
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    ngOnInit(): void {
        this.initialize();
        this.headless = this.webcomponentInterfaceService.headlessToBoolean();
        this.retrieveContentPages();
    }

    getRootUrl(): string {
        if (this.applicationSettingsService.serviceOnly) {
            return StateNamesEnum.SERVICE_ONLY;
        }

        return StateNamesEnum.COURT_AND_SERVICE;
    }

    clearStorage(): void {
        this.sessionStorageService.clearWorkflowAndBooking();
        this.stateService.init();
    }

    getHeaderColor(color: string): string | null {
        document.documentElement.style.setProperty('--pikaday-selected-bg-color', this.header2Color);
        return color?.trim() || null;
    }
}
